import { useEffect, useState } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { useAuth0 } from '@brighte/passwordless-authenticator';
import { Header } from '@components/Header';
import { useDatadogAddError } from '@hooks';
import { usePackageContext } from '@provider';
import { Button } from '@spark-web/button';
import { Container } from '@spark-web/container';
import { Heading } from '@spark-web/heading';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { CONTACT_NUMBER_BY_PARTNER } from '@utils/constants';
import ErrorImage from '../../assets/error.svg?react';
import { getErrorDetails } from './helper';

export function ErrorFallback({ error, resetErrorBoundary }: FallbackProps) {
  const { clearCredentials } = useAuth0();
  const { logError } = useDatadogAddError();
  const { packageForm } = usePackageContext();
  const { enterprisePartnerId } = packageForm;
  const contactNumber = CONTACT_NUMBER_BY_PARTNER[enterprisePartnerId] ?? CONTACT_NUMBER_BY_PARTNER['default'];

  const { code, message } = getErrorDetails(error);
  const [showAlert, setShowAlert] = useState(false);

  const handleConfirm = () => {
    clearCredentials();
    resetErrorBoundary();
    setShowAlert(false);
  };

  useEffect(() => {
    if (message === 'UNAUTHENTICATED') {
      setShowAlert(true);
    }
    logError(error);
  }, [message, logError, error]);

  return (
    <>
      {showAlert && (
        <ContentDialog title="Login" isOpen={true} onToggle={() => setShowAlert(false)}>
          <Text>
            Your session has expired or you have been logged out. Please log in again to continue the application.
          </Text>
          <Button onClick={handleConfirm}>Continue</Button>
        </ContentDialog>
      )}
      {!showAlert && (
        <Container size="medium">
          <Stack gap="xxlarge" padding="xlarge" marginBottom="xlarge">
            <Stack gap="xxlarge" alignSelf="center">
              <Header />
            </Stack>
            <Stack align="center" gap="xlarge" marginTop="xxlarge">
              <ErrorImage />
            </Stack>
            <Stack align="center" gap="xlarge">
              <Heading level="1">An error occurred</Heading>
              <Text align="center">{message}</Text>
              <Text>If you’re still experiencing issues, call the team at Brighte on {contactNumber}</Text>
              {code && <Text tone="muted">Technical error {code}</Text>}
            </Stack>
          </Stack>
        </Container>
      )}
    </>
  );
}
